"use client"

/**
 * Add Questions shell secondary nav — Bank / Blueprint / Question in Section.
 * Uses the same `OutlineTreeMenu` folder tree as the library rail
 * (`LibraryFolderTreeBranch` family) via `FoldersTab` in `bank-side-panel.tsx`.
 */

import * as React from "react"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"

import { BankEmptyState } from "@/components/assessment-structure/bank-empty-state"
import {
  FoldersTab,
  type BankNavGroup,
} from "@/components/assessment-structure/bank-side-panel"
import { BlueprintPanel } from "@/components/assessment-structure/blueprint-panel"
import type { BlueprintGapProgress } from "@/components/assessment-structure/blueprint-panel-shared"
import type { BlueprintTargetIntent } from "@/components/assessment-structure/blueprint-panel"
import { QuestionPreviewProvider } from "@/components/assessment-structure/question-preview-context"
import { SectionQuestionTable } from "@/components/assessment-structure/section-question-table"
import { SectionQuestionsTotals } from "@/components/assessment-structure/section-questions-totals"
import {
  SecondaryHubNavCompactShell,
  useSecondaryHubNavChrome,
} from "@/components/sidebar"
import { useSecondaryPanel } from "@/components/sidebar"
import { Button } from "@/components/ui/button"
import { Tip } from "@/components/ui/tip"
import { cn } from "@/lib/utils"
import type { DiscriminationMethodId } from "@/lib/discrimination"
import type {
  AssessmentBankQuestion,
} from "@/lib/mock/assessment-add-questions"
import type { MixBand } from "@/lib/section-mix"
import type { QuestionRecord } from "@/src/pages/assessment-details"
import {
  Tabs,
  TabsContent,
  TabsCountBadge,
  TabsList,
  TabsTrigger,
  TabsTriggerIcon,
  TabsTriggerLabel,
} from "@exxatdesignux/ui/components/tabs"
import { ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID } from "@/lib/assessment-question-bank-nav"
import type { SectionGapProgress } from "@/components/assessment-structure/bank-side-panel"

export type AssessmentQuestionBankLeftTab = "bank" | "blueprint" | "section"

type BankViewTabItem = {
  value: AssessmentQuestionBankLeftTab
  label: string
  icon: string
  count: number | null
}

function BankViewTabsChrome({
  items,
}: {
  value: AssessmentQuestionBankLeftTab
  items: readonly BankViewTabItem[]
}) {
  return (
    <TabsList ariaLabel="Bank views" variant="line" className="w-fit">
      {items.map((item) => (
        <TabsTrigger key={item.value} value={item.value} aria-label={item.label}>
          <TabsTriggerIcon>
            <i className={cn("fa-light", item.icon)} aria-hidden />
          </TabsTriggerIcon>
          <TabsTriggerLabel className="max-w-[9rem] truncate">
            {item.label}
          </TabsTriggerLabel>
          {item.count != null ? <TabsCountBadge count={item.count} /> : null}
        </TabsTrigger>
      ))}
    </TabsList>
  )
}

export type AssessmentQuestionBankBridge = {
  navGroups: BankNavGroup[]
  scope: string
  onScopeChange: (scope: string) => void
  leftTab: AssessmentQuestionBankLeftTab
  onLeftTabChange: (tab: AssessmentQuestionBankLeftTab) => void
  blueprintGapsLength: number
  assessmentGapProgress: SectionGapProgress[]
  assessmentRows: AssessmentBankQuestion[]
  discriminationMethodId: DiscriminationMethodId
  onDiscriminationMethodChange: (method: DiscriminationMethodId) => void
  onFocusBlueprintTarget: (
    entry: BlueprintGapProgress,
    intent: BlueprintTargetIntent,
  ) => void
  onSelectMixBand: (band: MixBand) => void
  activeMixFacetScope: string | null
  sectionId: string
  addedRows: AssessmentBankQuestion[]
  sectionPaperRows: QuestionRecord[]
  onSectionDragEnd: (event: DragEndEvent) => void
  onPreviewPaperRow: (row: QuestionRecord) => void
  onMoveSectionQuestion: (questionId: string, direction: -1 | 1) => void
  onRemovePaperRows: (questionIds: string[]) => void
  onEditPaperRow: (row: QuestionRecord) => void
  totalPoints: number
  estimatedMinutes: number
  needsReviewCount: number
}

function AssessmentQuestionBankPanelBody({
  bridge,
}: {
  bridge: AssessmentQuestionBankBridge
}) {
  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const tabItems: BankViewTabItem[] = [
    {
      value: "bank",
      label: "Bank",
      icon: "fa-folders",
      count: null,
    },
    {
      value: "blueprint",
      label: "Blueprint",
      icon: "fa-bullseye-arrow",
      count:
        bridge.blueprintGapsLength > 0
          ? bridge.assessmentGapProgress.filter((entry) => !entry.satisfied)
              .length
          : null,
    },
    {
      value: "section",
      label: "Question in Section",
      icon: "fa-list-ol",
      count: bridge.addedRows.length > 0 ? bridge.addedRows.length : null,
    },
  ]

  return (
    <Tabs
      value={bridge.leftTab}
      onValueChange={(value) =>
        bridge.onLeftTabChange(value as AssessmentQuestionBankLeftTab)
      }
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <div className="min-w-0 shrink-0">
        <BankViewTabsChrome value={bridge.leftTab} items={tabItems} />
      </div>

      <TabsContent
        value="bank"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 data-[state=inactive]:hidden"
      >
        <FoldersTab
          groups={bridge.navGroups}
          activeScope={bridge.scope}
          onScopeChange={bridge.onScopeChange}
        />
      </TabsContent>

      <TabsContent
        value="blueprint"
        className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
      >
        <BlueprintPanel
          gapProgress={bridge.assessmentGapProgress}
          rows={bridge.assessmentRows}
          discriminationMethodId={bridge.discriminationMethodId}
          onDiscriminationMethodChange={bridge.onDiscriminationMethodChange}
          onFocusTarget={bridge.onFocusBlueprintTarget}
          onSelectMixBand={bridge.onSelectMixBand}
          activeMixFacetScope={bridge.activeMixFacetScope}
        />
      </TabsContent>

      <TabsContent
        value="section"
        className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
      >
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
          {bridge.addedRows.length === 0 ? (
            <BankEmptyState
              density="panel"
              icon="fa-list-ol"
              title="No questions yet"
              description="Add from the pool on the right and they line up here, in the order students will see them."
            />
          ) : (
            <DndContext
              sensors={sectionSensors}
              collisionDetection={closestCenter}
              onDragEnd={bridge.onSectionDragEnd}
            >
              <SortableContext
                items={bridge.sectionPaperRows.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
              >
                <QuestionPreviewProvider onPreview={bridge.onPreviewPaperRow}>
                  <SectionQuestionTable
                    sectionId={bridge.sectionId}
                    questions={bridge.sectionPaperRows}
                    focusMode="analytics"
                    onMoveQuestion={bridge.onMoveSectionQuestion}
                    onRemoveQuestions={bridge.onRemovePaperRows}
                    onEditQuestion={bridge.onEditPaperRow}
                  />
                </QuestionPreviewProvider>
              </SortableContext>
            </DndContext>
          )}
        </div>
        {bridge.addedRows.length > 0 ? (
          <SectionQuestionsTotals
            questionCount={bridge.addedRows.length}
            totalPoints={bridge.totalPoints}
            estimatedMinutes={bridge.estimatedMinutes}
            needsReviewCount={bridge.needsReviewCount}
            className="bg-background"
          />
        ) : null}
      </TabsContent>
    </Tabs>
  )
}

function AssessmentQuestionBankCompactNav({
  bridge,
}: {
  bridge: AssessmentQuestionBankBridge
}) {
  const { openPanel } = useSecondaryPanel()
  const { dismissNavFlyout } = useSecondaryHubNavChrome()

  const tabs: {
    value: AssessmentQuestionBankLeftTab
    label: string
    icon: string
    count: number | null
  }[] = [
    { value: "bank", label: "Bank", icon: "fa-folders", count: null },
    {
      value: "blueprint",
      label: "Blueprint",
      icon: "fa-bullseye-arrow",
      count:
        bridge.blueprintGapsLength > 0
          ? bridge.assessmentGapProgress.filter((entry) => !entry.satisfied)
              .length
          : null,
    },
    {
      value: "section",
      label: "Question in Section",
      icon: "fa-list-ol",
      count: bridge.addedRows.length > 0 ? bridge.addedRows.length : null,
    },
  ]

  return (
    <SecondaryHubNavCompactShell
      ariaLabel="Question bank"
      panelId={ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID}
    >
      {tabs.map((item) => (
        <li key={item.value} className="flex w-full justify-center" role="none">
          <Tip label={item.label} side="right">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label={item.label}
              aria-pressed={bridge.leftTab === item.value}
              className={cn(
                bridge.leftTab === item.value &&
                  "bg-sidebar-accent text-sidebar-accent-foreground"
              )}
              onClick={() => {
                bridge.onLeftTabChange(item.value)
                openPanel(ASSESSMENT_QUESTION_BANK_SECONDARY_PANEL_ID)
                dismissNavFlyout()
              }}
            >
              <i className={`fa-light ${item.icon}`} aria-hidden />
              {item.count != null ? (
                <span className="sr-only">{item.count}</span>
              ) : null}
            </Button>
          </Tip>
        </li>
      ))}
    </SecondaryHubNavCompactShell>
  )
}

export function AssessmentQuestionBankSecondaryNav() {
  const { assessmentQuestionBankBridge } = useSecondaryPanel()
  const { showCompactRail } = useSecondaryHubNavChrome()

  if (!assessmentQuestionBankBridge) return null

  if (showCompactRail) {
    return (
      <AssessmentQuestionBankCompactNav bridge={assessmentQuestionBankBridge} />
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <AssessmentQuestionBankPanelBody bridge={assessmentQuestionBankBridge} />
    </div>
  )
}
